<template>
  <SectionTemplate class="data-showcase-section" title="超1000家头部企业见证" theme="dark">
    <div class="data-showcase-swiper-wrapper relative">
      <!-- Swiper 轮播容器 -->
      <div class="swiper-container">
        <swiper
          :free-mode="true"
          :modules="modules"
          :grab-cursor="true"
          :breakpoints="swiperBreakpoints"
          class="data-showcase-swiper"
          @init="initSwiper"
        >
          <swiper-slide v-for="(item, index) in showcaseData" :key="index">
            <div class="data-showcase-card flex flex-col items-center justify-center cursor-grab">
              <div class="data-showcase-card-logo">
                <Resource :src="item.brand" class="h-full" :alt="item.name" />
              </div>
              <div class="data-showcase-card-num flex items-center justify-center font-dt">
                <div
                  v-if="['up', 'down'].includes(item.trend)"
                  class="data-showcase-card-num-trend"
                >
                  <ArrowUp
                    class="data-showcase-card-num-trend-arrow"
                    filled
                    :class="item.trend === 'up' ? 'rotate-[0deg]' : 'rotate-[180deg]'"
                  />
                </div>
                <div>
                  {{ item.number }}
                </div>
              </div>
              <div class="data-showcase-card-label">{{ item.label }}</div>
              <div class="data-showcase-card-desc">{{ item.description }}</div>
              <button
                v-if="item.link"
                class="card-btn"
                @click.stop="() => handleOpenLink(item.link)"
              >
                了解更多
                <ArrowRight class="w-[1em] h-[1em] text-[18px]" />
              </button>
            </div>
          </swiper-slide>
        </swiper>
      </div>
      <div
        class="switch switch-prev absolute left-[-86px] top-1/2 translate-y-[-50%]"
        :class="{ disabled: swiperInstance?.isBeginning }"
        @click="slidePrev"
      >
        <ArrowRight class="text-[22px] rotate-[180deg]" />
      </div>
      <div
        class="switch switch-next absolute right-[-86px] top-1/2 translate-y-[-50%]"
        :class="{ disabled: swiperInstance?.isEnd }"
        @click="slideNext"
      >
        <ArrowRight class="text-[22px]" />
      </div>
    </div>

    <div class="data-showcase-btn-wrapper">
      <button class="btn-for-more" @click="handleStart">
        企业咨询
        <ArrowRight class="btn-for-more-arrow" />
      </button>
    </div>
  </SectionTemplate>
</template>

<script setup lang="ts">
import { PositionCode, useContactUs } from '@rendora/common/features/contact';
import { FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import ArrowRight from '~/assets/svg/icon-arrow-right.svg?component';
import ArrowUp from '~/assets/svg/icon-arrow-up.svg?component';
import Resource from '~/components/resource/index';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';

const modules = [FreeMode];

// 使用 breakpoints 配置响应式
const swiperBreakpoints = {
  // 移动端 (默认)
  0: {
    // slidesPerView: 1.15 as const,
    slidesPerView: 'auto' as const,
    spaceBetween: 16,
    freeMode: true,
    loop: false,
    slidesOffsetBefore: 20,
    slidesOffsetAfter: 20,
    // centeredSlides: true,
  },
  // PC端 (769px 及以上)
  769: {
    slidesPerView: 3,
    spaceBetween: 24,
    freeMode: false,
  },
};

const handleStart = () => {
  useContactUs(
    PositionCode['有言官网-企业咨询'],
    '领取行业应用案例',
    '更多行业落地应用案例，扫码领取',
  );
};

// 数据配置
const showcaseData = [
  {
    brand: getFeUrl('/youyan-platform/main/logo2/zx.png'),
    trend: 'up',
    number: '200%',
    label: '视频制作效率',
    description:
      '“我们用有言制作的精品课程，一经上线瞬间引起了公司各业务单位的关注，大家纷纷申请账号试用，甚至在内部形成了一股数字人课程开发的热潮，短时间内就成功产出数百门精品课程。”',
    link: 'https://youyan3d.com/customer-stories/ZTE-Break-4B-Efficiency-Cycle-of-Training-Video-Production',
    name: '中兴',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/skq.png'),
    trend: 'down',
    number: '98%',
    label: '视频制作成本',
    description:
      '“有言让斯凯奇彻底告别‘一页纸培训’时代。现在，我们能以更低成本、更短时间完成海量SKU产品培训视频制作。更关键是，AI数字人讲得清，员工学得快、动力足，门店转化也稳步提升。”',
    link: 'https://youyan3d.com/customer-stories/Skechers-Ride-AIGC-Trend-to-Power-Sports-with-Smart-Brain',
    name: '斯凯奇',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/dwzq.png'),
    trend: 'up',
    number: '230%',
    label: '平均完播率',
    description:
      '“借助有言，我们可以真正能够实现财经资讯高频持续运营，更重要的是，从数据上看，有言生成的AI数字人视频，不论是从用户停留时长还是完播率，都比真人拍摄的爆款视频更能打。”',
    link: 'https://youyan3d.com/customer-stories/Soochow-Securities-AIGC-Reshapes-Growth-Logic-with-Nearly-60-Growth',
    name: '东吴证券',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/zrt.png'),
    subBrand: 'CHANDO GROUP',
    number: '1000+',
    trend: 'none',
    label: 'SKU产品讲解视频',
    description:
      '“传统TVC制作成本高，大量非明星SKU推广资源有限。引入有言后，我们直接将1000+SKU图文海报转化为产品视频，让用户了解更多非明星单品的功效与成分，成功激活了SKU产品复购增量。”',
    link: 'https://youyan3d.com/customer-stories/CHANDO-AIGC-Activates-Over-200-Increase-in-Repurchase',
    name: '自然堂',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/haier.png'),
    number: '400+',
    trend: 'none',
    label: '内部员工广泛使用',
    description:
      '“有没有一款AI视频平台，能真正满足海尔8大产业部门、33个小微业务普遍存在的视频制作需求？有言做到了。如今，各部门可按需自主制作培训课程，几个月内已生成超千条视频。”',
    link: 'https://youyan3d.com/customer-stories/Haier-Group-AIGC-Digital-Efficiency-Employee-Dealer-Training',
    name: '海尔',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/aeyk.png'),
    trend: 'up',
    number: '10X',
    label: '社媒浏览量',
    description:
      '“过去，市场团队视频产能远远跟不上700多家门店的推广需求。引入有言后，我们重塑内容生产模式，总部统筹方向，分公司灵活复用，真正实现全平台矩阵化传播。',
    link: 'https://youyan3d.com/customer-stories/Aier-Eye-Hospital-Bulk-Reach-Precise-Customer-Locking-Cost-Drop-50',
    name: '爱尔眼科',
  },
];

const swiperInstance = ref<SwiperType | null>(null);

const initSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;
};

const slidePrev = () => {
  swiperInstance.value?.slidePrev();
};

const slideNext = () => {
  swiperInstance.value?.slideNext();
};

const handleOpenLink = (link: string) => {
  if (link) {
    window.open(link, '_blank');
  }
};
</script>

<style scoped lang="scss" src="./pc.scss" />
<style scoped lang="scss" src="./mobile.scss" />
